/* eslint-disable react/jsx-props-no-spreading */
import Box from '@mui/material/Box';
import Autocomplete, {
  AutocompleteRenderInputParams,
} from '@mui/material/Autocomplete';
import { CircularProgress, InputAdornment, TextField } from '@mui/material';
import React, { Fragment, useState } from 'react';
import clsx from 'clsx';

export interface AutomCompleteBarProps<T, P = any> {
  data: T[];
  onGetOptionLabel: (option: T) => string;
  renderOption: (option: T) => React.ReactNode;
  onChangeCapture?: React.FormEventHandler<HTMLDivElement>;
  onChange?: any;
  onInputChange?: any;
  inputLabel?: string;
  className?: string;
  inputClassName?: string;
  inputParams?: (params: AutocompleteRenderInputParams) => {
    value: string;
    placeholder?: string | null;
  };
  icon?: JSX.Element;
  groupBy?: (option: T) => string;
  groupsTitle?: P;
  emptyState: React.ReactNode;
  loading?: boolean;
  RenderInput?: any;
  error?: boolean;
  onBlur?: any;
  onFocus?: any;
  showDropdownArrow?: boolean;
}

const AutomCompleteBar = <T,>({
  data,
  onGetOptionLabel,
  renderOption,
  inputLabel,
  className,
  inputClassName,
  onChange,
  onChangeCapture,
  inputParams,
  icon,
  groupBy,
  groupsTitle,
  emptyState,
  onInputChange,
  loading,
  RenderInput,
  error = false,
  onBlur,
  onFocus,
  showDropdownArrow = false,
}: AutomCompleteBarProps<T>) => {
  const [open, setOpen] = useState(false);

  return (
    <div className={className}>
      <Autocomplete
        className="w-full"
        noOptionsText={emptyState}
        options={data}
        autoHighlight
        getOptionLabel={onGetOptionLabel}
        onChange={onChange}
        onChangeCapture={onChangeCapture}
        onBlur={onBlur}
        onFocus={onFocus}
        onInputChange={onInputChange}
        filterOptions={(x) => x}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        open={open}
        popupIcon={showDropdownArrow ? undefined : null}
        ListboxProps={{
          style: {
            maxHeight: '250px',
            overflow: 'auto',
          },
        }}
        renderOption={(props, option) => (
          <Box
            component="li"
            sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
            {...props}
          >
            {renderOption(option)}
          </Box>
        )}
        renderGroup={(params) => (
          <Fragment key={params.key}>
            {groupsTitle && groupsTitle[params.group] && (
              <p className="text-sm m-0 mb-1 p-0 px-2">
                {groupsTitle[params.group]}
              </p>
            )}
            <p>{params.children}</p>
          </Fragment>
        )}
        groupBy={groupBy}
        renderInput={(params) => {
          const customProps = (inputParams && inputParams(params)) || {
            value: '',
          };

          const placeholderValue =
            customProps.placeholder !== null
              ? customProps.placeholder || ''
              : '';

          const inputProps = {
            ...params.inputProps,
            autoComplete: 'off',
          };

          if (showDropdownArrow) {
            const textFieldParams = { ...params };

            if (customProps.value !== undefined) {
              textFieldParams.inputProps = {
                ...textFieldParams.inputProps,
                value: customProps.value,
              };
            }

            return (
              <TextField
                {...textFieldParams}
                placeholder={placeholderValue}
                className={clsx(
                  inputClassName,
                  error ? '!border-red-500 !text-red-500' : ''
                )}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: icon ? (
                    <InputAdornment position="start">{icon}</InputAdornment>
                  ) : null,
                  endAdornment: (
                    <>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                variant="outlined"
                error={error}
                label={inputLabel}
              />
            );
          }

          return (
            <div
              ref={params.InputProps.ref}
              className={clsx(
                inputClassName,
                'flex items-center border-solid border-[1.5px] px-2 border-[--colors-primary-900] rounded-[--radius-2] p-1',
                error ? '!border-red-500 !text-red-500 !border-2' : ''
              )}
            >
              {icon && <InputAdornment position="start">{icon}</InputAdornment>}
              {inputLabel && <label htmlFor="autocomplete">{inputLabel}</label>}
              {RenderInput ? (
                <RenderInput
                  id="autocomplete"
                  {...inputProps}
                  {...customProps}
                  placeholder={placeholderValue}
                />
              ) : (
                <input
                  id="autocomplete"
                  {...inputProps}
                  {...customProps}
                  placeholder={placeholderValue}
                  className="focus:outline-none !px-1 py-2 !border-none text-gray-600 text-md !w-full"
                />
              )}
              {loading ? <CircularProgress color="inherit" size={20} /> : null}
            </div>
          );
        }}
      />
    </div>
  );
};

export default AutomCompleteBar;
